import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { patientsApi } from '../services/api';
import './clinic.css';
import {italianDay,loadStudioAppointments} from '../services/studioAgenda';

const aiExamples = [
  'Ho un nuovo paziente e devo preparare la prima visita con BIA.',
  'Analizza l’andamento di un paziente e indicami cosa controllare.',
  'Prepara una bozza di piano alimentare personalizzato.'
];

const directActions = [
  { to: '/patients', state: { openCreate: true }, icon: 'patient', title: 'Nuovo paziente', text: 'Crea la cartella e prepara la prima visita' },
  { to: '/bia', icon: 'bia', title: 'Nuova misura BIA', text: 'Acquisisci o registra una misurazione' },
  { to: '/diets', icon: 'plan', title: 'Piano alimentare', text: 'Apri piani, modelli e proposte' },
  { to: '/sistema-ts', icon: 'receipt', title: 'Prestazione TS', text: 'Controlla documenti e invii sanitari' }
];

function DashboardActionIcon({ name }) {
  const paths = {
    agenda: <><path d="M7 7.5h10"/><path d="M8.5 4.5v4"/><path d="M15.5 4.5v4"/><path d="M5.5 8.5h13"/><rect x="5" y="6" width="14" height="13" rx="2.5"/><path d="M8.5 12h2.5M13 12h2.5M8.5 15.5H11"/></>,
    arrow: <><path d="M7 12h10"/><path d="m13 8 4 4-4 4"/></>,
    patient: <><circle cx="12" cy="8.5" r="3"/><path d="M6.5 18.5c1-3 3-4.5 5.5-4.5s4.5 1.5 5.5 4.5"/><path d="M18 9v4M16 11h4"/></>,
    bia: <><path d="M5.5 15.5h13"/><path d="M7 12.5c1.4-3.2 3-4.8 5-4.8s3.6 1.6 5 4.8"/><path d="M12 7.7l2.2 4.1"/><circle cx="12" cy="12.5" r=".8"/></>,
    plan: <><path d="M7.5 5.5h7.5l2.5 2.5v10.5h-10z"/><path d="M15 5.5V8h2.5"/><path d="M9.5 11h5M9.5 14h5M9.5 17h3"/></>,
    receipt: <><path d="M7 5.5h10v13l-2-1.2-2 1.2-2-1.2-2 1.2z"/><path d="M9.5 9h5M9.5 12h5M9.5 15h3"/></>
  };
  return <svg className={`dashboard-action-icon dashboard-action-icon-${name}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">{paths[name]}</svg>;
}

export function Dashboard() {
  const navigate = useNavigate();
  const commandRef = useRef(null);
  const [command, setCommand] = useState('');
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const now = new Date();
      const [patientData, appointmentData] = await Promise.all([
        patientsApi.getAll(),
        loadStudioAppointments(now)
      ]);
      setPatients(patientData || []);
      setAppointments(appointmentData || []);
      setHasLoaded(true);
    } catch (requestError) {
      setError(requestError.message || 'Non è stato possibile aggiornare la panoramica.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); const refresh=()=>{if(!document.hidden)load();}; const timer=setInterval(refresh,60000); window.addEventListener('focus',refresh); return()=>{clearInterval(timer);window.removeEventListener('focus',refresh);}; }, [load]);

  const today = appointments.filter(item => italianDay(item.startsAt) === italianDay(new Date()));

  const startWithSibilla = event => {
    event.preventDefault();
    const initialMessage = command.trim();
    if (!initialMessage) {
      commandRef.current?.focus();
      return;
    }
    navigate('/copilot', { state: { initialMessage, autoSubmit: true } });
  };

  const chooseExample = example => {
    setCommand(example);
    commandRef.current?.focus();
  };

  return <main className="clinic-page dashboard-state-surface dashboard-ai-page" aria-busy={loading}>
    <section className="dashboard-ai-hero" aria-labelledby="dashboardAiTitle">
      <div className="dashboard-ai-intro">
        <span className="dashboard-ai-mark" aria-hidden="true">S</span>
        <h1 id="dashboardAiTitle">Descrivi il lavoro clinico.<br/>Sibilla prepara il percorso.</h1>
        <p>Nuovo paziente, prima visita, BIA, andamento o piano alimentare: scrivi la richiesta come la diresti a una collega.</p>
        <div className="dashboard-ai-safety"><span aria-hidden="true"/><strong>Tu mantieni il controllo</strong><small>Nessun dato viene salvato senza la tua conferma.</small></div>
      </div>
      <form className="dashboard-ai-composer" onSubmit={startWithSibilla}>
        <label htmlFor="dashboardAiCommand">Cosa vuoi preparare oggi?</label>
        <textarea ref={commandRef} id="dashboardAiCommand" value={command} onChange={event => setCommand(event.target.value)} placeholder="Es. Mostrami l’andamento di Flavio Perrone e prepara una nuova proposta alimentare…" rows="4" maxLength="4000"/>
        <div className="dashboard-ai-examples" aria-label="Richieste di esempio">
          {aiExamples.map((example, index) => <button key={example} type="button" onClick={() => chooseExample(example)}><span>{index + 1}</span>{example}</button>)}
        </div>
        <div className="dashboard-ai-submit"><small>Sibilla analizzerà la cartella e proporrà i prossimi passi.</small><button type="submit" disabled={!command.trim()}>Prepara con Sibilla</button></div>
      </form>
    </section>

    {error && <div className="dashboard-state-error" role="alert"><div><strong>Panoramica non aggiornata</strong><span>{error} I dati mostrati potrebbero non essere aggiornati.</span></div><button type="button" onClick={load} disabled={loading}>{loading ? 'Nuovo tentativo…' : 'Riprova'}</button></div>}

    <section className="dashboard-context" aria-label="Situazione dello studio" aria-live="polite">
      {loading && !hasLoaded ? [0, 1, 2, 3].map(index => <div className="dashboard-context-item dashboard-state-skeleton" key={index}><span/><strong/><small/></div>)
        : !hasLoaded ? <>
          <div className="dashboard-context-item"><span>Pazienti attivi</span><strong>—</strong><small>Dati non disponibili</small></div>
          <div className="dashboard-context-item"><span>Oggi</span><strong>—</strong><small>Appuntamenti</small></div>
          <div className="dashboard-context-item"><span>Prossimi 7 giorni</span><strong>—</strong><small>Visite</small></div>
          <div className="dashboard-context-item"><span>Strumenti clinici</span><strong>3</strong><small>BIA · Piani · Sistema TS</small></div>
        </> : <>
          <div className="dashboard-context-item"><span>Pazienti attivi</span><strong>{patients.length}</strong><small>Archivio personale</small></div>
          <div className="dashboard-context-item"><span>Oggi</span><strong>{today.length}</strong><small>Appuntamenti</small></div>
          <div className="dashboard-context-item"><span>Prossimi 7 giorni</span><strong>{appointments.length}</strong><small>Visite programmate</small></div>
          <div className="dashboard-context-item"><span>Strumenti clinici</span><strong>3</strong><small>BIA · Piani · Sistema TS</small></div>
        </>}
    </section>

    <div className="clinic-grid two dashboard-secondary"><section className="clinic-panel"><div className="clinic-panel-head"><h2>Prossimi appuntamenti</h2><Link className="dashboard-panel-action" to="/agenda"><DashboardActionIcon name="agenda"/><span>Apri agenda</span></Link></div>
      {loading && !hasLoaded
        ? <div className="dashboard-state-loading" role="status">Sto caricando i prossimi appuntamenti…</div>
        : !hasLoaded
          ? <div className="clinic-empty">Appuntamenti non disponibili. Usa “Riprova” per aggiornare la panoramica.</div>
          : appointments.length
            ? <div className="timeline-list">{appointments.slice(0, 6).map(item => <div className="timeline-row" key={item.id}><div><strong>{new Date(item.startsAt).toLocaleDateString('it-IT',{timeZone:'Europe/Rome'})}</strong><br/><small>{new Date(item.startsAt).toLocaleTimeString('it-IT', { timeZone:'Europe/Rome', hour: '2-digit', minute: '2-digit' })}</small></div><div><strong>{item.displayName || (item.patient ? `${item.patient.lastName} ${item.patient.firstName}` : 'Appuntamento')}</strong><br/><small>{item.type}</small></div><span className="clinic-pill">{item.status}</span></div>)}</div>
            : <div className="clinic-empty">Nessun appuntamento nei prossimi sette giorni.</div>}
    </section>
      <section className="clinic-panel"><div className="clinic-panel-head"><div><h2>Azioni dirette</h2><p>Per operazioni già definite, senza passare dall’assistente.</p></div></div><div className="dashboard-action-grid">{directActions.map(action => <Link key={action.to} className="dashboard-action-tile" to={action.to} state={action.state}><span className="dashboard-action-mark"><DashboardActionIcon name={action.icon}/></span><span className="dashboard-action-copy"><strong>{action.title}</strong><small>{action.text}</small></span><DashboardActionIcon name="arrow"/></Link>)}</div></section></div>
  </main>;
}
